package fr.frux.feature.site.create

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.content.SiteLimits
import fr.frux.shared.domain.model.PlantingCount
import fr.frux.shared.domain.model.PlantingDraft
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.SiteDraft
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

/**
 * Création d'un lieu de cueillette (ADR 0035). La clé d'idempotence est tirée à la construction
 * et gardée tant que le brouillon renvoyé est celui qui a échoué : un renvoi après coupure ne crée
 * jamais un second lieu. Un brouillon modifié après l'échec est une autre création, il prend une
 * clé neuve (le serveur rejouerait sinon la réponse du premier).
 */
class CreateSiteViewModel(
    private val args: CreateSiteArgs,
    private val siteRepository: SiteRepository,
    private val speciesRepository: SpeciesRepository,
    private val newIdempotencyKey: () -> String = { Uuid.random().toString() },
) : ViewModel() {
    private val _state = MutableStateFlow(CreateSiteState())
    val state: StateFlow<CreateSiteState> = _state.asStateFlow()

    private val _events = Channel<CreateSiteEvent>(Channel.BUFFERED)
    val events: Flow<CreateSiteEvent> = _events.receiveAsFlow()

    private var idempotencyKey: String = newIdempotencyKey()
    private var lastSentDraft: SiteDraft? = null

    // Référentiel déjà chargé par SpeciesRepository (cache/ETag) : la recherche filtre en local.
    private var referential: List<Species> = emptyList()

    init {
        viewModelScope.launch {
            speciesRepository
                .getSpecies()
                .onSuccess {
                    referential = it
                    refreshResults()
                }.onFailure { Logger.d(TAG, "Référentiel espèces indisponible : ${it.message}") }
        }
    }

    fun onNameChange(value: String) = _state.update { it.copy(name = value.take(SiteLimits.NAME_MAX)) }

    fun onSpeciesQuery(value: String) {
        _state.update { it.copy(speciesQuery = value) }
        refreshResults()
    }

    fun addSpecies(species: Species) {
        _state.update { s ->
            if (s.plantings.size >= SiteLimits.PLANTINGS_MAX || s.plantings.any { it.species.id == species.id }) {
                s
            } else {
                s.copy(plantings = s.plantings + PlantingLine(species, count = null))
            }
        }
        refreshResults()
    }

    fun setCount(
        speciesId: Long,
        count: PlantingCount,
    ) = _state.update { s ->
        s.copy(plantings = s.plantings.map { if (it.species.id == speciesId) it.copy(count = count) else it })
    }

    fun removeSpecies(speciesId: Long) {
        _state.update { s -> s.copy(plantings = s.plantings.filterNot { it.species.id == speciesId }) }
        refreshResults()
    }

    fun onAccess(choice: SiteAccessChoice) = _state.update { it.copy(access = choice) }

    fun onManagerName(value: String) = _state.update { it.copy(managerName = value.take(SiteLimits.MANAGER_NAME_MAX)) }

    fun onHomeToggled(value: Boolean) = _state.update { it.copy(isHome = value) }

    fun submit() {
        val current = _state.value
        // Un second appui pendant l'envoi ne part pas : une seule requête en vol.
        if (!current.canSubmit) return
        val draft = current.toDraft()
        if (lastSentDraft != null && draft != lastSentDraft) idempotencyKey = newIdempotencyKey()
        lastSentDraft = draft
        val key = idempotencyKey
        _state.update { it.copy(submission = CreateSiteSubmission.Sending) }
        viewModelScope.launch {
            siteRepository
                .createSite(draft, key)
                .onSuccess { site ->
                    _events.send(CreateSiteEvent.Created(site))
                }.onFailure { error ->
                    // Refus attendu (réseau, quota, saisie) sous le niveau erreur : pas pour Sentry (#310).
                    if (error.isExpectedFailure) {
                        Logger.i(TAG, "Création du lieu refusée : ${error.message}")
                    } else {
                        Logger.e(TAG, "Échec de la création du lieu", error)
                    }
                    _state.update { it.copy(submission = CreateSiteSubmission.Failed(error.asAppError())) }
                }
        }
    }

    fun requestExit() {
        // Pendant l'envoi, quitter laisserait partir la requête et créerait un lieu sans retour à l'écran.
        if (_state.value.submission == CreateSiteSubmission.Sending) return
        if (_state.value.hasInputs) {
            _state.update { it.copy(exitPrompt = true) }
        } else {
            confirmExit()
        }
    }

    fun confirmExit() {
        _state.update { it.copy(exitPrompt = false) }
        _events.trySend(CreateSiteEvent.Exited)
    }

    fun dismissExit() = _state.update { it.copy(exitPrompt = false) }

    private fun refreshResults() {
        _state.update { s ->
            val query = s.speciesQuery.trim()
            val listed = s.plantings.map { it.species.id }.toSet()
            val results =
                if (query.isEmpty()) {
                    emptyList()
                } else {
                    referential
                        .filter { it.selectable && it.id !in listed && it.matches(query) }
                        .take(MAX_RESULTS)
                }
            s.copy(speciesResults = results)
        }
    }

    private fun CreateSiteState.toDraft(): SiteDraft =
        SiteDraft(
            name = name.trim(),
            latitude = args.latitude,
            longitude = args.longitude,
            accessRule =
                when {
                    isHome -> SiteAccessRule.PRIVATE
                    access == SiteAccessChoice.ASK -> SiteAccessRule.ASK
                    else -> SiteAccessRule.OPEN
                },
            managerName = if (isHome) null else managerName.trim().ifEmpty { null },
            isHome = isHome,
            plantings = plantings.mapNotNull { line -> line.count?.let { PlantingDraft(line.species.id, it) } },
        )

    private companion object {
        const val TAG = "CreateSiteViewModel"
        const val MAX_RESULTS = 8
    }
}
